/**
 * Utilidades para el manejo de posicionamiento y dimensiones del chatbot
 */

export interface ChatDimensions {
  width: number;
  height: number;
  posX: number;
  posY: number;
}

export interface ResizeConstraints {
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
}

export class ChatbotPositionHelper {
  /**
   * Calcula las dimensiones iniciales del chat basadas en el viewport
   * Altura: 60% del viewport con máximo de 700px
   */
  static getInitialDimensions(chatWidth: number = 410): ChatDimensions {
    const height = Math.min(window.innerHeight * 0.6, 700);
    return {
      width: chatWidth,
      height,
      posX: window.innerWidth - chatWidth - 32,
      posY: window.innerHeight - height - 16,
    };
  }

  /**
   * Ajusta las dimensiones y posición del chat cuando cambia el tamaño de la ventana
   */
  static adjustOnWindowResize(current: ChatDimensions): ChatDimensions {
    const newHeight = Math.min(window.innerHeight * 0.6, 700);
    const adjustedWidth = Math.min(current.width, window.innerWidth - 32);

    return {
      width: adjustedWidth,
      height: newHeight,
      posX: window.innerWidth - adjustedWidth - 32,
      posY: window.innerHeight - newHeight - 16,
    };
  }

  /**
   * Limita la posición dentro de los límites de la pantalla
   */
  static constrainPosition(
    x: number,
    y: number,
    dimensions: ChatDimensions,
  ): { x: number; y: number } {
    const maxX = window.innerWidth - dimensions.width;
    const maxY = window.innerHeight - dimensions.height;

    return {
      x: Math.max(0, Math.min(x, maxX)),
      y: Math.max(0, Math.min(y, maxY)),
    };
  }
}

export class ChatbotResizeHelper {
  /**
   * Calcula las nuevas dimensiones al redimensionar desde el borde derecho
   */
  static resizeRight(
    startWidth: number,
    dx: number,
    constraints: ResizeConstraints,
  ): number {
    return Math.max(
      constraints.minWidth,
      Math.min(startWidth + dx, constraints.maxWidth),
    );
  }

  /**
   * Calcula las nuevas dimensiones al redimensionar desde el borde izquierdo
   */
  static resizeLeft(
    startWidth: number,
    startPosX: number,
    dx: number,
    constraints: ResizeConstraints,
  ): { width: number; posX: number } {
    let newWidth = Math.max(constraints.minWidth, startWidth - dx);
    let newPosX = startPosX + dx;

    // No dejar que se salga por la izquierda
    if (newPosX < 0) {
      newWidth += newPosX;
      newPosX = 0;
    }

    const maxWidth = window.innerWidth - newPosX;
    newWidth = Math.max(constraints.minWidth, Math.min(newWidth, maxWidth));

    // Ajustar posición si el ancho está en el mínimo
    if (
      newWidth === constraints.minWidth &&
      newPosX + constraints.minWidth > window.innerWidth
    ) {
      newPosX = window.innerWidth - constraints.minWidth;
    }

    return { width: newWidth, posX: newPosX };
  }

  /**
   * Calcula las nuevas dimensiones al redimensionar desde el borde inferior
   */
  static resizeBottom(
    startHeight: number,
    dy: number,
    constraints: ResizeConstraints,
  ): number {
    return Math.max(
      constraints.minHeight,
      Math.min(startHeight + dy, constraints.maxHeight),
    );
  }

  /**
   * Calcula las nuevas dimensiones al redimensionar desde el borde superior
   */
  static resizeTop(
    startHeight: number,
    startPosY: number,
    dy: number,
    constraints: ResizeConstraints,
  ): { height: number; posY: number } {
    let newHeight = Math.max(constraints.minHeight, startHeight - dy);
    let newPosY = startPosY + dy;

    // No dejar que se salga por arriba
    if (newPosY < 0) {
      newHeight += newPosY;
      newPosY = 0;
    }

    // Ajustar posición si la altura está en el mínimo
    if (
      newHeight === constraints.minHeight &&
      newPosY + constraints.minHeight > window.innerHeight
    ) {
      newPosY = window.innerHeight - constraints.minHeight;
    }

    return { height: newHeight, posY: newPosY };
  }

  /**
   * Obtiene las restricciones de redimensión basadas en la posición actual
   */
  static getConstraints(
    startPosX: number,
    startPosY: number,
  ): ResizeConstraints {
    return {
      minWidth: 300,
      minHeight: 200,
      maxWidth: window.innerWidth - startPosX,
      maxHeight: window.innerHeight - startPosY,
    };
  }
}
